import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from './Card';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';

export type StatCardTone =
  | 'blue'
  | 'sky'
  | 'emerald'
  | 'green'
  | 'amber'
  | 'orange'
  | 'red'
  | 'rose'
  | 'violet'
  | 'teal'
  | 'slate';

export interface StatCardProps {
  label: string;
  value: string | number;
  /** Optional secondary line (e.g. "vs last month") */
  subtitle?: string;
  /** Trend indicator: positive = green arrow up, negative = red arrow down, neutral = dash */
  trend?: 'positive' | 'negative' | 'neutral';
  /** Trend label, e.g. "+12%" */
  trendLabel?: string;
  /** Icon to show in the top-right corner */
  icon?: React.ReactNode;
  /** Link/action when the card is clicked */
  onClick?: () => void;
  /** Visual accent applied to the icon chip and selected state */
  tone?: StatCardTone;
  /** Selected/active styling for filter-like cards */
  selected?: boolean;
  /** Tooltip text shown on hover */
  tooltip?: string;
  className?: string;
}

const trendConfig = {
  positive: { Icon: TrendingUp, colorClass: 'text-green-600 bg-green-50' },
  negative: { Icon: TrendingDown, colorClass: 'text-red-600 bg-red-50' },
  neutral: { Icon: Minus, colorClass: 'text-slate-500 bg-slate-100' },
};

const metricValueFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 20,
});

const toneConfig: Record<StatCardTone, { bar: string; icon: string; selected: string; hover: string }> = {
  blue: {
    bar: 'from-blue-500/0 via-blue-500/70 to-cyan-400/0',
    icon: 'border-blue-200/70 bg-blue-50 text-blue-700',
    selected: 'border-blue-300 bg-blue-50/60 ring-2 ring-blue-200 shadow-md',
    hover: 'hover:border-blue-200',
  },
  sky: {
    bar: 'from-sky-500/0 via-sky-500/70 to-cyan-400/0',
    icon: 'border-sky-200/70 bg-sky-50 text-sky-700',
    selected: 'border-sky-300 bg-sky-50/60 ring-2 ring-sky-200 shadow-md',
    hover: 'hover:border-sky-200',
  },
  emerald: {
    bar: 'from-emerald-500/0 via-emerald-500/70 to-teal-400/0',
    icon: 'border-emerald-200/70 bg-emerald-50 text-emerald-700',
    selected: 'border-emerald-300 bg-emerald-50/60 ring-2 ring-emerald-200 shadow-md',
    hover: 'hover:border-emerald-200',
  },
  green: {
    bar: 'from-green-500/0 via-green-500/70 to-lime-400/0',
    icon: 'border-green-200/70 bg-green-50 text-green-700',
    selected: 'border-green-300 bg-green-50/60 ring-2 ring-green-200 shadow-md',
    hover: 'hover:border-green-200',
  },
  amber: {
    bar: 'from-amber-500/0 via-amber-500/80 to-orange-400/0',
    icon: 'border-amber-200/70 bg-amber-50 text-amber-700',
    selected: 'border-amber-300 bg-amber-50/70 ring-2 ring-amber-200 shadow-md',
    hover: 'hover:border-amber-200',
  },
  orange: {
    bar: 'from-orange-500/0 via-orange-500/75 to-amber-400/0',
    icon: 'border-orange-200/70 bg-orange-50 text-orange-700',
    selected: 'border-orange-300 bg-orange-50/70 ring-2 ring-orange-200 shadow-md',
    hover: 'hover:border-orange-200',
  },
  red: {
    bar: 'from-red-500/0 via-red-500/75 to-rose-400/0',
    icon: 'border-red-200/70 bg-red-50 text-red-700',
    selected: 'border-red-300 bg-red-50/70 ring-2 ring-red-200 shadow-md',
    hover: 'hover:border-red-200',
  },
  rose: {
    bar: 'from-rose-500/0 via-rose-500/75 to-pink-400/0',
    icon: 'border-rose-200/70 bg-rose-50 text-rose-700',
    selected: 'border-rose-300 bg-rose-50/70 ring-2 ring-rose-200 shadow-md',
    hover: 'hover:border-rose-200',
  },
  violet: {
    bar: 'from-violet-500/0 via-violet-500/75 to-fuchsia-400/0',
    icon: 'border-violet-200/70 bg-violet-50 text-violet-700',
    selected: 'border-violet-300 bg-violet-50/70 ring-2 ring-violet-200 shadow-md',
    hover: 'hover:border-violet-200',
  },
  teal: {
    bar: 'from-teal-500/0 via-teal-500/75 to-cyan-400/0',
    icon: 'border-teal-200/70 bg-teal-50 text-teal-700',
    selected: 'border-teal-300 bg-teal-50/70 ring-2 ring-teal-200 shadow-md',
    hover: 'hover:border-teal-200',
  },
  slate: {
    bar: 'from-slate-500/0 via-slate-500/65 to-slate-400/0',
    icon: 'border-slate-200/80 bg-slate-100 text-slate-700',
    selected: 'border-slate-300 bg-slate-100/80 ring-2 ring-slate-200 shadow-md',
    hover: 'hover:border-slate-300',
  },
};

export function StatCard({
  label,
  value,
  subtitle,
  trend,
  trendLabel,
  icon,
  onClick,
  tone = 'blue',
  selected = false,
  tooltip,
  className,
}: StatCardProps) {
  const hasTrend = trend !== undefined && trendLabel !== undefined;
  const { Icon: TrendIcon, colorClass } = trend ? trendConfig[trend] : trendConfig.neutral;
  const toneStyles = toneConfig[tone];
  const displayValue = typeof value === 'number' ? metricValueFormatter.format(value) : value;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }

  const card = (
    <Card
      className={cn(
        // `container-type: inline-size` turns the card into a query container so
        // the label/value type below can scale with the card rather than the
        // viewport — narrow cards shrink the text instead of clipping it.
        'group relative isolate flex min-h-[112px] flex-col gap-3 overflow-hidden border-border/80 bg-card/95 transition-all duration-200 [container-type:inline-size]',
        toneStyles.hover,
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg',
        selected && toneStyles.selected,
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? selected : undefined}
      onKeyDown={handleKeyDown}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r',
          toneStyles.bar,
        )}
        aria-hidden="true"
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[length:clamp(0.625rem,6cqi,0.6875rem)] font-semibold uppercase leading-tight tracking-[0.14em] text-muted-foreground [overflow-wrap:anywhere]">
            {label}
          </p>
        </div>
        {icon && (
          <div
            className={cn(
              'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border shadow-sm',
              toneStyles.icon,
            )}
          >
            {icon}
          </div>
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[length:clamp(1.125rem,11cqi,1.5rem)] font-semibold leading-none tracking-tight text-foreground tabular-nums">
            {displayValue}
          </p>
          {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
        </div>

        {hasTrend && (
          <span
            className={cn(
              'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
              colorClass,
            )}
          >
            <TrendIcon className="h-3 w-3" aria-hidden="true" />
            {trendLabel}
          </span>
        )}
      </div>
    </Card>
  );

  if (!tooltip) return card;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  );
}
